import { Link } from "react-router-dom";
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { getNextIntake, cohorts, Cohort, getCohortStatus } from '@/data/cohorts';
import { cohortDetails } from '@/lib/data/cohorts';
import { Price, PriceDisplay, SavingDisplay } from '@/components/ui/Price';
import { useRegion } from '@/contexts/RegionContext';
import { isZarRegion, products } from '@/data/pricing';

function formatDeadline(isoDate: string): string {
  if (!isoDate) return '';
  const dateStr = isoDate.includes('T') ? isoDate.split('T')[0] : isoDate;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return isoDate;
  const [year, month, day] = parts;
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface CertificationCardProps {
  levelNumber: 1 | 2;
  title: string;
  productKey: 'practitioner-certification' | 'team-facilitator-certification';
  nextCohort: Cohort | null;
  furtherCohorts: Cohort[];
  certificationPath: string;
}

function CertificationCard({
  levelNumber,
  title,
  productKey,
  nextCohort,
  furtherCohorts,
  certificationPath
}: CertificationCardProps) {
  const { regionCode, locale } = useRegion();
  const isZar = isZarRegion(regionCode, locale);
  const isOpenCohort = nextCohort && getCohortStatus(nextCohort) === 'Open';

  return (
    <div className="bg-white p-8 border border-ground-deep shadow-sm flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold tracking-widest uppercase text-rule-3">
            Level {levelNumber}
          </span>
          <Link
            to={certificationPath}
            className="text-xs font-semibold text-ink/60 hover:text-rule-3 uppercase tracking-wider transition-colors"
          >
            View curriculum &rarr;
          </Link>
        </div>

        <h2 className="text-2xl font-serif text-ink mb-6">{title}</h2>

        {isOpenCohort ? (
          <>
            <div className="mb-6 p-4 bg-ground/50 border border-ground-deep/60">
              <p className="text-xs uppercase tracking-widest text-rule-3 font-bold mb-1">
                Confirmed Intake
              </p>
              <p className="text-3xl md:text-4xl font-serif text-ink font-bold leading-tight">
                Next intake: {nextCohort.displayDate}
              </p>
            </div>

            <div className="space-y-4 mb-6 text-ink/80 text-sm">
              <div>
                <span className="font-semibold text-ink block text-xs uppercase tracking-wider mb-1">
                  Session Schedule
                </span>
                <p className="text-sm text-ink/80 leading-relaxed">
                  {nextCohort.sessionSchedule || nextCohort.schedule}
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6 p-4 bg-ground/50 border border-ground-deep/60">
              <p className="text-xs uppercase tracking-widest text-rule-3 font-bold mb-1">
                Intake Status
              </p>
              <p className="text-2xl md:text-3xl font-serif text-ink font-bold leading-tight">
                New dates coming soon
              </p>
            </div>

            <div className="space-y-3 mb-6 text-ink/80 text-sm leading-relaxed">
              <p>
                Intakes are scheduled in small cohorts. New confirmed dates will be announced shortly.
              </p>
            </div>
          </>
        )}

        <div className="my-6 pt-4 border-t border-ground-deep">
          {products[productKey]?.note && (
            <p className="text-xs text-ink/60 mb-2">
              {products[productKey].note}
            </p>
          )}
          <Price productKey={productKey} />
        </div>

        {!isOpenCohort && (
          <div className="mb-6 p-4 bg-ground border border-ground-deep text-xs text-ink/90 leading-relaxed">
            Your intake date will be confirmed within five working days of enrolment. If the confirmed date does not suit you, you may cancel for a full refund.
          </div>
        )}
      </div>

      <div>
        {isOpenCohort ? (
          <Link
            to={`/register?product=${productKey}`}
            className="block text-center bg-ink text-white px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-ink/70 transition-colors mb-6"
          >
            Enrol in Next Cohort
          </Link>
        ) : (
          <Link
            to={`/register?product=${productKey}`}
            className="block text-center bg-ink text-white px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-ink/70 transition-colors mb-6"
          >
            Enrol on Request
          </Link>
        )}

        {isOpenCohort && furtherCohorts.length > 0 ? (
          <div className="pt-4 border-t border-ground-deep">
            <p className="text-xs uppercase tracking-wider font-semibold text-ink/70 mb-2">
              Further Confirmed Dates
            </p>
            <ul className="space-y-1.5 text-sm">
              {furtherCohorts.map((c) => (
                <li key={c.id || c.slug} className="flex justify-between items-baseline text-ink/80">
                  <span className="font-medium text-ink">{c.displayDate}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="pt-4 border-t border-ground-deep">
            <p className="text-xs uppercase tracking-wider font-semibold text-ink/70 mb-1">
              Cohort Schedule
            </p>
            <p className="text-xs text-ink/60">
              Cohorts are scheduled on request once registrations reach four delegates.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function CohortsPage() {
  const { regionCode, locale } = useRegion();
  const isZar = isZarRegion(regionCode, locale);
  const nextL1 = getNextIntake(1);
  const nextL2 = getNextIntake(2);

  const furtherL1 = cohorts.filter(
    (c) => c.level === 1 && c.id !== nextL1?.id && getCohortStatus(c) === 'Open'
  );
  const furtherL2 = cohorts.filter(
    (c) => c.level === 2 && c.id !== nextL2?.id && getCohortStatus(c) === 'Open'
  );

  const openCohortDetails = cohortDetails.filter((item) => getCohortStatus(item) === 'Open');

  const bundleProduct = products['certification-bundle'];
  const savings = bundleProduct.savings;

  return (
    <main>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-8">
        <Breadcrumb
          items={[
            { label: 'For Coaches and Practitioners', href: '/practitioners' },
            { label: 'Upcoming Intakes' }
          ]}
        />
      </div>

      <section className="pt-32 pb-16 px-6 max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-ink mb-6">
          Upcoming certification intakes
        </h1>
        <p className="text-xl text-ink/70/80 mb-12 max-w-3xl leading-relaxed">
          Intakes are scheduled in small cohorts to ensure personalised mentoring, deep case study discussions, and active coaching feedback.
        </p>

        {/* Certification Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          <CertificationCard
            levelNumber={1}
            title="Practitioner Certification"
            productKey="practitioner-certification"
            nextCohort={nextL1}
            furtherCohorts={furtherL1}
            certificationPath="/practitioner-certification"
          />

          <CertificationCard
            levelNumber={2}
            title="Team Facilitator Certification"
            productKey="team-facilitator-certification"
            nextCohort={nextL2}
            furtherCohorts={furtherL2}
            certificationPath="/certification/team-facilitator"
          />

          <div className="bg-white p-8 border border-ground-deep shadow-sm flex flex-col justify-between md:col-span-2 lg:col-span-1">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold tracking-widest uppercase text-rule-3">
                  Level 1 + Level 2
                </span>
              </div>

              <h2 className="text-2xl font-serif text-ink mb-6">
                {bundleProduct.name}
              </h2>

              <div className="space-y-3 mb-6 text-ink/80 text-sm leading-relaxed">
                <p>
                  Take Level 1 and Level 2 together and <SavingDisplay prefix="save" />. Level 1 is completed first; Level 2 follows in the next Team Facilitator cohort.
                </p>
              </div>

              <div className="my-6 pt-4 border-t border-ground-deep">
                <Price productKey="certification-bundle" />
              </div>
            </div>

            <div>
              <Link
                to="/register?product=certification-bundle"
                className="block text-center bg-ink text-white px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-ink/70 transition-colors"
              >
                Enrol in both
              </Link>
            </div>
          </div>
        </div>

        <div className="mb-16 p-6 bg-white border border-ground-deep text-base text-ink leading-relaxed">
          Additional cohorts are scheduled on request for groups of four or more.{' '}
          <Link
            to="/contact"
            className="text-rule-3 font-semibold underline hover:text-ink transition-colors"
          >
            Contact us
          </Link>{' '}
          to arrange one.
        </div>

        {/* Confirmed Cohorts Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif text-ink mb-6">
            Detailed Cohort Schedules
          </h2>
          {openCohortDetails.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {openCohortDetails.map((item) => (
                <div key={item.slug} className="bg-white p-6 border border-ground-deep shadow-sm flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-rule-3 block mb-2">
                      {item.monthYear} Intake
                    </span>
                    <h3 className="text-xl font-serif text-ink font-bold mb-3">
                      {item.certificationName}
                    </h3>
                    <p className="text-xs font-semibold text-rule-3 mb-2">
                      Starts {item.displayDate}
                    </p>
                    <p className="text-sm text-ink/70 mb-4 leading-relaxed line-clamp-2">
                      {item.description}
                    </p>
                    <p className="text-xs text-ink/60 font-medium mb-6">
                      {item.schedule}
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-ground-deep">
                    <div className="text-sm font-bold text-ink min-h-[1.5rem] flex items-center">
                      <PriceDisplay productKey={item.slug?.includes('team') ? 'team-facilitator-certification' : 'practitioner-certification'} />
                    </div>
                    <Link
                      to={`/cohorts/${item.slug}`}
                      className="text-xs font-bold uppercase tracking-wider text-rule-3 hover:text-ink transition-colors underline"
                    >
                      View Cohort Details &rarr;
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white p-8 border border-ground-deep text-center">
              <p className="text-lg font-serif text-ink font-semibold mb-2">New dates coming soon</p>
              <p className="text-sm text-ink/70">Check back shortly or contact us to schedule a private cohort.</p>
            </div>
          )}
        </section>

        {/* What happens after you enrol */}
        <section className="mb-16 pt-8 border-t border-ground-deep">
          <h2 className="text-2xl md:text-3xl font-serif text-ink mb-8">
            What happens after you enrol
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 border border-ground-deep shadow-sm">
              <div className="text-xs font-bold tracking-widest uppercase text-rule-3 mb-2">
                Phase 1
              </div>
              <h3 className="text-lg font-serif text-ink font-semibold mb-3">
                Confirmation & Joining Details
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Immediately receive your formal enrolment confirmation, tax invoice, orientation pack, and calendar invitations containing your live Zoom sessions schedule.
              </p>
            </div>

            <div className="bg-white p-6 border border-ground-deep shadow-sm">
              <div className="text-xs font-bold tracking-widest uppercase text-rule-3 mb-2">
                Phase 2
              </div>
              <h3 className="text-lg font-serif text-ink font-semibold mb-3">
                Pre-Course Access
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Gain immediate access to our digital learning portal with 8 to 10 hours of self-directed foundational reading, Enneagram theory, and introductory exercises.
              </p>
            </div>

            <div className="bg-white p-6 border border-ground-deep shadow-sm">
              <div className="text-xs font-bold tracking-widest uppercase text-rule-3 mb-2">
                Phase 3
              </div>
              <h3 className="text-lg font-serif text-ink font-semibold mb-3">
                Your Profile Feedback Session
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Complete your full personal FIVE LENS assessment and experience a private, one-to-one debrief with an accredited Master Moderator prior to cohort kick-off.
              </p>
            </div>

            <div className="bg-white p-6 border border-ground-deep shadow-sm">
              <div className="text-xs font-bold tracking-widest uppercase text-rule-3 mb-2">
                Phase 4
              </div>
              <h3 className="text-lg font-serif text-ink font-semibold mb-3">
                The Live Sessions
              </h3>
              <p className="text-sm text-ink/70 leading-relaxed">
                Join your cohort for interactive live Zoom masterclasses featuring real-client case studies, triad practice, written assignment reviews, and graduation presentations.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-white p-6 border border-ground-deep text-sm text-ink/80">
          <p className="leading-relaxed">
            <strong className="text-ink font-semibold">Need to defer?</strong> We understand professional commitments can change. If you are unable to attend your allocated cohort, you may request a deferral to a subsequent scheduled intake. Please consult our full deferral and cancellation terms in our{' '}
            <Link
              to="/terms-of-service"
              className="text-rule-3 font-semibold underline hover:text-ink transition-colors"
            >
              Terms of Service
            </Link>
            .
          </p>
        </section>
      </section>
    </main>
  );
}

export default CohortsPage;
